import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Buildings, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getUtm } from "@/components/AttendeeFields";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHero, Section, SectionHead, Loader } from "@/components/Blocks";
import { EventCard } from "@/components/EventCard";
import { Agenda } from "@/components/Agenda";
import { Input } from "@/components/ui/input";
import { PriceCards } from "@/components/PriceCards";

const proj = (lat, lng) => ({ x: ((lng + 126) / 66) * 1000, y: ((57 - lat) / 16) * 360 });
const TZ = { "America/Vancouver": "Pacific", "America/Edmonton": "Mountain", "America/Regina": "Central (Saskatchewan)", "America/Winnipeg": "Central", "America/Toronto": "Eastern", "America/Halifax": "Atlantic", "America/Moncton": "Atlantic" };

const TourMap = ({ cities }) => {
  const [hover, setHover] = useState(null);
  return (
    <div className="dark-panel relative overflow-hidden p-4" data-testid="tour-map">
      <svg viewBox="0 0 1000 360" className="w-full" role="img" aria-label="Map of tour cities across Canada">
        <defs>
          <pattern id="g" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0H0V40" fill="none" stroke="rgba(96,165,250,0.12)" /></pattern>
        </defs>
        <rect width="1000" height="360" fill="url(#g)" />
        <circle cx="500" cy="180" r="170" fill="none" stroke="rgba(30,142,90,0.25)" />
        <circle cx="500" cy="180" r="100" fill="none" stroke="rgba(30,142,90,0.2)" />
        {cities.map((c) => {
          const p = proj(c.lat, c.lng);
          const on = hover === c.slug;
          return (
            <Link key={c.slug} to={`/cities/${c.slug}`} onMouseEnter={() => setHover(c.slug)} onMouseLeave={() => setHover(null)} data-testid={`map-city-${c.slug}`}>
              <circle cx={p.x} cy={p.y} r={on ? 10 : 6} fill={c.next_event ? "#E0A21B" : "#64748b"} stroke="#0B1B33" strokeWidth="2"><title>{c.name}</title></circle>
              {on && <text x={p.x + 12} y={p.y - 10} fill="#fff" fontSize="16" fontFamily="Barlow Condensed" fontWeight="700">{c.name.toUpperCase()}</text>}
            </Link>
          );
        })}
      </svg>
      <div className="mt-2 flex gap-5 text-xs text-white/60">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#E0A21B]" /> Dates announced</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-500" /> Coming soon</span>
      </div>
    </div>
  );
};

export function Cities() {
  const { data, loading } = useFetch("/cities");
  return (
    <>
      <PageHero eyebrow="Canadian tour" title="Pick your launch pad" sub="Twenty cities and counting. Tap a city to see dates, venue and seats left." />
      <Section>
        {loading ? <Loader /> : (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-7"><TourMap cities={data} /></div>
            <div className="grid content-start gap-2 sm:grid-cols-2 lg:col-span-5">
              {data.map((c) => (
                <Link key={c.slug} to={`/cities/${c.slug}`} data-testid={`city-link-${c.slug}`}
                  className="flex items-center justify-between panel px-4 py-3 transition-colors hover:border-[#E0A21B]">
                  <span className="flex items-center gap-2 font-medium"><MapPin size={16} weight="fill" className="text-[#E0A21B]" />{c.name}, {c.province}</span>
                  <span className="font-mono-num text-xs text-[#475569]">{c.next_event ? fmtDate(c.next_event.start_at, c.timezone) : "TBA"}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Section>
      <Section tone="light">
        <div id="fall-2027" className="grid gap-8 lg:grid-cols-12" data-testid="fall-tour-section">
          <div className="lg:col-span-8">
            {(data || []).some((c) => c.fall_event?.registration_open)
              ? <SectionHead eyebrow="Registration open" title="Fall 2027 Return Tour" sub="Every city returns in fall 2027. Book your seat now." />
              : <SectionHead eyebrow="Coming soon – registration closed" title="Fall 2027 Return Tour" sub="Every city returns in fall 2027. Registration opens soon." />}
            <div className="grid gap-2 sm:grid-cols-2">
              {(data || []).filter((c) => c.fall_event).sort((a, b) => a.fall_event.start_at.localeCompare(b.fall_event.start_at)).map((c) => (
                <Link key={c.slug} to={`/events/${c.fall_event.slug}`} className="panel flex items-center justify-between gap-2 px-4 py-3" data-testid={`fall-city-${c.slug}`}>
                  <span className="font-medium">{c.name}, {c.province}<span className="block font-mono-num text-xs text-[#475569]">{fmtDate(c.fall_event.start_at, c.timezone, { weekday: "short" })}</span></span>
                  {c.fall_event.registration_open ? <span className="rounded-full bg-[#1E8E5A]/15 px-2.5 py-1 text-[11px] font-bold uppercase text-[#1E8E5A]" data-testid={`fall-city-status-${c.slug}`}>Book now</span> : <span className="rounded-full bg-[#E2E8F0] px-2.5 py-1 text-[11px] font-bold uppercase text-[#475569]" data-testid={`fall-city-status-${c.slug}`}>{c.fall_event.opens_at ? `Opens ${fmtDate(c.fall_event.opens_at, c.timezone, { year: undefined })}` : "Registration opens soon"}</span>}
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4"><WaitlistForm cta="city-request" requestCity title="Notify me for my city" /></div>
        </div>
      </Section>
    </>
  );
}

export const WaitlistForm = ({ citySlug, eventId, title = "Join the waitlist", cta, season = "", requestCity = false }) => {
  const [f, setF] = useState({ name: "", email: "", consent: false, requested_city: "" });
  const [done, setDone] = useState(false);
  const needConsent = cta === "registration-open" || requestCity;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { type: "waitlist", ...f, season, city_slug: citySlug || "", event_id: eventId || "", cta: cta || (eventId ? "event-waitlist" : "city-notify-me"), utm: getUtm() });
      setDone(true);
      toast.success(cta === "registration-open" ? "We'll email you when registration opens" : cta === "lectures-return" ? "We'll let you know when lectures return" : "You're on the list");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (done) return <div className="panel p-6" data-testid="waitlist-success">Thanks. We will email you when seats or new dates open.</div>;
  return (
    <form onSubmit={submit} className="panel space-y-3 p-6" data-testid="waitlist-form">
      <h3 className="font-display text-2xl font-bold uppercase">{title}</h3>
      <Input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="waitlist-name-input" />
      <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="waitlist-email-input" />
      {requestCity && <Input required placeholder="Your city" value={f.requested_city} onChange={(e) => setF({ ...f, requested_city: e.target.value })} data-testid="waitlist-requested-city-input" />}
      <label className="flex items-start gap-2 text-xs text-[#475569]">
        <input type="checkbox" required={needConsent} checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="waitlist-consent-checkbox" className="mt-0.5" />
        Yes, email me about registration and new dates in my city. I can unsubscribe at any time.
      </label>
      <button className="btn-navy w-full" data-testid="waitlist-submit-btn">Notify me</button>
    </form>
  );
};

export function CityPage() {
  const { slug } = useParams();
  const { data: c, loading, error } = useFetch(`/cities/${slug}`);
  if (loading) return <Loader />;
  if (error) return <Section><p>{error}</p></Section>;
  const cur = c.events.filter((e) => e.season !== "fall-2027" || e.registration?.open);
  const fall = c.events.filter((e) => e.season === "fall-2027" && !e.registration?.open);
  const open = cur.find((e) => e.registration?.open);
  return (
    <>
      <PageHero eyebrow={`${c.province} · ${TZ[c.timezone] || c.timezone} time`} title={c.name} sub="Class date, agenda and updates for this tour stop." />
      <Section>
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHead eyebrow="Upcoming" title={`Classes in ${c.name}`} />
            {cur.length === 0 ? <p className="text-[#475569]">No open dates this season.</p> : (
              <div className="grid gap-4 md:grid-cols-2">{cur.map((e, i) => <EventCard key={e.id} e={e} i={i} />)}</div>
            )}
            {fall.length > 0 && (
              <div className="panel mt-6 p-5" data-testid="city-returning">
                <div className="eyebrow">Fall 2027 Return Tour</div>
                {fall.map((e) => (
                  <div key={e.id} className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-display text-xl font-bold uppercase" data-testid="city-returning-date">Returning: {fmtDate(e.start_at, c.timezone, { weekday: "short" })}</span>
                    <Link to={`/events/${e.slug}`} className="rounded-full bg-[#E2E8F0] px-3 py-1 text-xs font-bold uppercase text-[#475569]">{e.registration.opens_at ? `Registration opens ${fmtDate(e.registration.opens_at, c.timezone)}` : "Registration opens soon"}</Link>
                  </div>
                ))}
                <div className="mt-4"><WaitlistForm citySlug={c.slug} season="fall-2027" cta="registration-open" title="Notify me when registration opens" /></div>
              </div>
            )}
            {c.scheduled.map((s) => (
              <div key={s.id} className="panel mt-4 p-5" data-testid={`scheduled-${s.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-display text-2xl font-bold uppercase text-[#0B1B33]" data-testid="scheduled-date"><CalendarBlank className="text-[#E0A21B]" />{fmtDate(s.start_at, c.timezone, { weekday: "long", month: "long" })}</span>
                  <span className="rounded-full bg-[#E0A21B]/15 px-3 py-1 text-xs font-bold uppercase text-[#9A6B00]">Registration opens soon</span>
                </div>
                {s.title.includes(" – ") && <div className="mt-1 text-sm font-semibold">{s.title.split(" – ").pop()}</div>}
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#475569]"><span>Venue being confirmed</span><span>Price: TBA</span></div>
                {s.agenda && <div className="mt-4"><Agenda agenda={s.agenda} doorsOpen={s.doors_open} tz={c.timezone} compact /></div>}
              </div>
            ))}
            <div className="mt-10">
              {c.venues.length > 0 && <div className="eyebrow">Venues</div>}
              {c.venues.map((v) => (
                <div key={v.id} className="mt-3 flex items-start gap-3 panel p-4">
                  <Buildings size={24} className="text-[#E0A21B]" />
                  <div><div className="font-semibold">{v.name}</div><div className="text-sm text-[#475569]">{v.address}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-6 lg:col-span-4">
            {open && <div className="panel p-5" data-testid="city-price-cards"><div className="eyebrow mb-3">Ticket prices</div><PriceCards compact tiers={open.price_tiers} tz={c.timezone} tierLeft={open.tier_left} vipBenefits={open.vip_benefits} /></div>}
            {fall.length === 0 && !cur.length && <WaitlistForm citySlug={c.slug} cta="lectures-return" title={`Notify me when lectures return to ${c.name}`} />}
          </div>
        </div>
      </Section>
    </>
  );
}
